import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useApiFetch } from "../../api/client";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import { Chip } from "../../components/ui/Chip";
import { applyCodePick, isCodeFieldKey } from "./codePicks";
import { errorMessage, monthName } from "./format";
import { InfoToggle } from "./InfoToggle";
import { LogField } from "./LogField";
import { LogHistory } from "./LogHistory";
import { TAB_BY_KEY, TAB_BY_KIND, TABS } from "./logTabs";
import { SectionHeader } from "./SectionHeader";
import {
  CODE_SETS,
  type CodeSetName,
  type LogEntry,
  type Lookups,
  type NumberedLogRequest,
  type RefCode,
  type ResinContext,
  type RosterSupplier,
  type TabKey,
  type Values,
} from "./types";

interface Props {
  month: string;
  resin: ResinContext;
  canWrite: boolean;
  onLogChanged?: () => void;
  logRequest?: NumberedLogRequest | null;
}

interface Editing {
  kind: LogEntry["kind"];
  id: number;
}

export function LogIntelligence({ month, resin, canWrite, onLogChanged, logRequest }: Props) {
  const presetValuesRef = useRef<Values | null>(null);
  const [formResetNumber, setFormResetNumber] = useState(0);
  const { resinFamilyCode } = resin;
  const apiFetch = useApiFetch();
  const api = useRef(apiFetch);
  api.current = apiFetch;
  const { confirm, dialog } = useConfirmDialog();

  const [lookups, setLookups] = useState<Lookups | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("price");
  const [values, setValues] = useState<Values>({});
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [history, setHistory] = useState<LogEntry[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const cfg = TAB_BY_KEY[tab];

  const loadRoster = useCallback(async (): Promise<RosterSupplier[]> => {
    const res = await api.current("consultant-cockpit/roster");
    return res.json();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [codeLists, roster] = await Promise.all([
          Promise.all(
            CODE_SETS.map(async (set) => (await api.current(`reference/${set}`)).json() as Promise<RefCode[]>),
          ),
          loadRoster(),
        ]);
        if (cancelled) return;
        const codes = Object.fromEntries(CODE_SETS.map((set, i) => [set, codeLists[i]])) as Record<
          CodeSetName,
          RefCode[]
        >;
        setLookups({ codes, roster });
      } catch (err) {
        if (!cancelled) setLoadError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRoster]);

  const refreshHistory = useCallback(async () => {
    try {
      const res = await api.current(
        `consultant-cockpit/log?month=${month}-01&resinFamily=${encodeURIComponent(resinFamilyCode)}`,
      );
      setHistory(await res.json());
      setHistoryError(null);
    } catch (err) {
      setHistoryError(errorMessage(err));
    }
  }, [month, resinFamilyCode]);

  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);

  useEffect(() => {
    setEditing(null);
    setMsg(null);
  }, [resinFamilyCode]);

  useEffect(() => {
    if (!lookups || editing) return;
    setValues({ ...TAB_BY_KEY[tab].defaults(month, lookups), ...presetValuesRef.current });
    presetValuesRef.current = null;
  }, [tab, month, lookups, editing, resinFamilyCode, formResetNumber]);

  const switchTab = (key: TabKey) => {
    setTab(key);
    setEditing(null);
    setMsg(null);
  };

  const onChange = (key: string, value: string) =>
    setValues((currentValues) =>
      isCodeFieldKey(key)
        ? applyCodePick(currentValues, key, value, resin)
        : { ...currentValues, [key]: value },
    );

  const openEntry = async (entry: LogEntry) => {
    if (!entry.kind || entry.id === undefined || !entry.month) return;
    const target = TAB_BY_KEY[TAB_BY_KIND[entry.kind]];
    try {
      const res = await api.current(
        `${target.path}?month=${entry.month}&resinFamily=${encodeURIComponent(resinFamilyCode)}`,
      );
      const records: unknown[] = await res.json();
      const record = records.find((r) => target.recordId(r) === entry.id);
      if (!record) {
        setMsg("That record is no longer available. It may have been voided.");
        return;
      }
      setTab(target.key);
      setEditing({ kind: entry.kind, id: entry.id });
      setValues(target.fromRecord(record));
      setMsg(null);
    } catch (err) {
      setMsg(errorMessage(err));
    }
  };

  const openEntryRef = useRef(openEntry);
  openEntryRef.current = openEntry;

  useEffect(() => {
    if (!logRequest) return;
    if (logRequest.recordId !== null) {
      void openEntryRef.current({ kind: logRequest.kind, id: logRequest.recordId, month: logRequest.recordMonth });
      return;
    }
    presetValuesRef.current = logRequest.presetValues ?? null;
    setTab(TAB_BY_KIND[logRequest.kind]);
    setEditing(null);
    setMsg(null);
    setFormResetNumber((resetNumber) => resetNumber + 1);
  }, [logRequest]);

  const cancelEdit = () => {
    setEditing(null);
    setMsg(null);
  };

  const saveRoster = async () => {
    if (!lookups) return;
    const name = (values.supplierName ?? "").trim().toLowerCase();
    const existing = lookups.roster.find((s) => (s.supplierName ?? "").toLowerCase() === name);
    if (existing?.supplierId !== undefined) {
      await api.current(`${cfg.path}/${existing.supplierId}`, {
        method: "PATCH",
        body: JSON.stringify(cfg.toPatchBody?.(values)),
      });
    } else {
      await api.current(cfg.path, {
        method: "POST",
        body: JSON.stringify(cfg.toBody(values, month, resinFamilyCode)),
      });
    }
    setLookups({ ...lookups, roster: await loadRoster() });
    setValues(cfg.defaults(month, lookups));
    setMsg("Saved to the supplier roster.");
    onLogChanged?.();
  };

  const saveRecord = async () => {
    const body =
      editing && cfg.toPatchBody ? cfg.toPatchBody(values) : cfg.toBody(values, month, resinFamilyCode);
    const res = await api.current(editing ? `${cfg.path}/${editing.id}` : cfg.path, {
      method: editing ? "PATCH" : "POST",
      body: JSON.stringify(body),
    });
    const alreadyLogged = !editing && res.status === 200;
    const saved = cfg.savedMonth(values, month);
    setEditing(null);
    if (lookups) setValues(cfg.defaults(month, lookups));
    const where = alreadyLogged
      ? `Already logged: this exact entry was saved to ${monthName(saved)} earlier.`
      : `Saved to ${monthName(saved)}.`;
    setMsg(saved === month ? where : `${where} Switch the month to see it in that month's history.`);
    await refreshHistory();
    onLogChanged?.();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving || !canWrite) return;
    const problem = cfg.validate?.(values) ?? null;
    if (problem) {
      setMsg(problem);
      return;
    }
    setSaving(true);
    setMsg("Saving…");
    try {
      if (tab === "roster") await saveRoster();
      else await saveRecord();
    } catch (err) {
      setMsg(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const voidRecord = async () => {
    if (!editing) return;
    const reason = await confirm({
      title: `Void this ${cfg.label.toLowerCase()}?`,
      message: "It stays on record as voided and drops out of the monthly history.",
      reasonLabel: "Reason",
      reasonPlaceholder: "e.g. duplicate entry",
      confirmLabel: "Void",
    });
    if (reason === null) return;
    if (!reason.trim()) {
      setMsg("A reason is required to void a record.");
      return;
    }
    try {
      await api.current(`${cfg.path}/${editing.id}/void`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim() }),
      });
      setEditing(null);
      setMsg("Voided.");
      await refreshHistory();
      onLogChanged?.();
    } catch (err) {
      setMsg(errorMessage(err));
    }
  };

  if (loadError) {
    return (
      <section className="lane on" aria-labelledby="log-heading">
        <SectionHeader sectionId="log-section" />
        <div className="empty">
          <span className="material-symbols-sharp">error</span>Couldn't reach the API: {loadError}
        </div>
      </section>
    );
  }

  if (!lookups) {
    return (
      <section className="lane on" aria-labelledby="log-heading">
        <SectionHeader sectionId="log-section" />
        <div className="empty">
          <span className="material-symbols-sharp spin">progress_activity</span>Loading reference data…
        </div>
      </section>
    );
  }

  const noTrackedSuppliers = tab === "nomination" && !lookups.roster.some((s) => s.isTracked);

  return (
    <section className="lane on" aria-labelledby="log-heading">
      <SectionHeader sectionId="log-section">
        <Chip>{monthName(month)}</Chip>
        <Chip variant="ok">{resinFamilyCode}</Chip>
      </SectionHeader>

      <div className="seg w-fit mb-2" role="group" aria-label="Record type">
        {TABS.map((t) => (
          <button key={t.key} type="button" aria-pressed={tab === t.key} onClick={() => switchTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      <InfoToggle label="What is this?" className="mb-5">
        <p className="m-0" aria-live="polite">
          {cfg.description}
        </p>
      </InfoToggle>

      <form className="card" onSubmit={onSubmit}>
        <div className="form">
          {cfg.fields.map((def) => (
            <LogField
              key={def.key}
              def={def}
              values={values}
              lookups={lookups}
              resin={resin}
              readOnly={!canWrite}
              onChange={onChange}
            />
          ))}
        </div>

        {tab === "roster" && lookups.roster.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {lookups.roster.map((s) => (
              <button
                key={s.supplierId}
                type="button"
                className="bg-transparent border-none p-0"
                onClick={() =>
                  setValues({ supplierName: s.supplierName ?? "", isTracked: s.isTracked ? "yes" : "no" })
                }
                title="Load into the form"
              >
                <Chip variant={s.isTracked ? "ok" : "default"}>
                  {s.supplierName}
                  {s.isTracked ? "" : " · not tracked"}
                </Chip>
              </button>
            ))}
          </div>
        )}

        <div className="formfoot">
          <span className="msg">
            {msg ??
              (!canWrite
                ? "Viewers can read the log but not add to it."
                : noTrackedSuppliers
                  ? "Add a supplier on the Supplier roster tab before logging a nomination."
                  : cfg.help)}
          </span>
          {editing && (
            <button type="button" className="btn ghost" onClick={cancelEdit}>
              <span className="material-symbols-sharp">close</span>Cancel Edit
            </button>
          )}
          {editing && cfg.voidable && canWrite && (
            <button type="button" className="btn ghost" onClick={voidRecord}>
              <span className="material-symbols-sharp">block</span>Void
            </button>
          )}
          <button type="submit" className="btn" disabled={!canWrite || saving || noTrackedSuppliers}>
            <span className="material-symbols-sharp">save</span>
            {saving ? "Saving…" : editing ? "Save Changes" : cfg.saveLabel}
          </button>
        </div>
      </form>

      <LogHistory
        month={month}
        resin={resin}
        entries={history}
        error={historyError}
        selected={editing}
        onSelect={openEntry}
      />

      {dialog}
    </section>
  );
}
